import { useState, useEffect, useMemo, useCallback } from "react";
import axios from "axios";
import { useLang } from "@/context/LanguageContext";
import { useAccount } from "@/context/AccountAuthContext";
import { PHOTO_NICHE } from "@/lib/assets";
import { Plus, Trash2, Check, Sandwich, Loader2, PartyPopper, AlertTriangle, ArrowLeft, CreditCard, CalendarDays, BadgeCheck, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SEASON_START = new Date(2026, 6, 6); // July 6 2026
const SEASON_END = new Date(2026, 7, 31); // Aug 31 2026

function pad(n) { return String(n).padStart(2, "0"); }
function iso(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

// Fallback: always show the summer weekday calendar even if the backend has no camp dates yet.
function buildDefaultDates() {
  const out = [];
  const d = new Date(SEASON_START);
  while (d <= SEASON_END) {
    const wd = d.getDay();
    if (wd >= 1 && wd <= 5) out.push({ date: iso(d), morning: true, afternoon: true, capacity: 6 });
    d.setDate(d.getDate() + 1);
  }
  return out;
}

function buildMonths(campDates) {
  const months = {};
  for (const cd of campDates) {
    const [y, m, day] = cd.date.split("-").map(Number);
    const d = new Date(y, m - 1, day);
    const key = `${y}-${m - 1}`;
    if (!months[key]) months[key] = { month: m - 1, year: y, days: [] };
    months[key].days.push({ dateObj: d, ...cd });
  }
  return Object.values(months).sort((a, b) => (a.year - b.year) || (a.month - b.month));
}

function computeAge(dob) {
  if (!dob) return null;
  const b = new Date(dob);
  if (isNaN(b.getTime())) return null;
  const ref = SEASON_START;
  let age = ref.getFullYear() - b.getFullYear();
  const m = ref.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < b.getDate())) age -= 1;
  return age;
}


function groupFor(age, groups) {
  if (age == null) return null;
  return groups.find((g) => age >= g.min && age <= g.max) || null;
}

let childSeq = 1;

export default function Booking({ mode = "camp", embedded = false }) {
  const { t, lang } = useLang();
  const account = useAccount();
  const isAtelier = mode === "atelier";
  const datesEndpoint = isAtelier ? "/atelier-dates" : "/camp-dates";
  const returnPath = isAtelier ? "/ateliers" : "/reservation";
  const pageTitle = isAtelier ? t.workshops.regTitle : t.booking.title;
  const pageSubtitle = isAtelier ? t.workshops.regSubtitle : t.booking.subtitle;

  const [config, setConfig] = useState(null);
  const [booked, setBooked] = useState({});
  const [campDates, setCampDates] = useState([]);
  const [parent, setParent] = useState({ name: "", email: "", phone: "" });
  const [emergency, setEmergency] = useState({ name: "", phone: "" });
  const [children, setChildren] = useState([{ id: childSeq++, first_name: "", last_name: "", dob: "", allergies: "" }]);
  const [activeChild, setActiveChild] = useState(children[0].id);
  const [consents, setConsents] = useState({ image_rights: false, outdoor_activities: false, swimming: false, emergency_transport: false, resident: false, authorized_pickup: "" });
  // selections: { childId: Set("date|period") }
  const [selections, setSelections] = useState({ [children[0].id]: new Set() });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [payState, setPayState] = useState(null);
  const [bookingResult, setBookingResult] = useState(null);

  const [isMember, setIsMember] = useState(false);
  const [checkingMember, setCheckingMember] = useState(false);
  const [settings, setSettings] = useState({});
  const [periods, setPeriods] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [apiAgeGroups, setApiAgeGroups] = useState(null);

  useEffect(() => { axios.get(`${API}/site-settings`).then((r) => setSettings(r.data || {})).catch(() => {}); }, []);
  useEffect(() => { axios.get(`${API}/age-groups`).then((r) => setApiAgeGroups(r.data.age_groups || [])).catch(() => setApiAgeGroups(null)); }, []);
  useEffect(() => {
    if (account?.user) {
      setParent((p) => ({ name: p.name || account.user.name || "", email: p.email || account.user.email || "", phone: p.phone || account.user.phone || "" }));
    }
  }, [account?.user]);
  useEffect(() => {
    if (isAtelier) return;
    axios.get(`${API}/vacation-periods`).then((r) => setPeriods(r.data.periods || [])).catch(() => setPeriods([]));
  }, [isAtelier]);

  // Kids Camp: dates limited to the selected vacation period
  const visiblePeriods = useMemo(() => {
    const today = iso(new Date());
    return periods.filter((p) => !p.end_date || p.end_date >= today);
  }, [periods]);

  useEffect(() => {
    if (isAtelier || activePeriod || visiblePeriods.length === 0) return;
    const withDates = visiblePeriods.find((p) => campDates.some((d) => (!p.start_date || d.date >= p.start_date) && (!p.end_date || d.date <= p.end_date)));
    setActivePeriod((withDates || visiblePeriods[0]).id);
  }, [visiblePeriods, campDates, isAtelier, activePeriod]);

  const displayDates = useMemo(() => {
    if (isAtelier || !activePeriod) return campDates;
    const p = periods.find((x) => x.id === activePeriod);
    if (!p) return campDates;
    return campDates.filter((d) => (!p.start_date || d.date >= p.start_date) && (!p.end_date || d.date <= p.end_date));
  }, [campDates, periods, activePeriod, isAtelier]);

  const months = useMemo(() => buildMonths(displayDates), [displayDates]);
  const campMap = useMemo(() => {
    const m = {};
    for (const cd of campDates) m[cd.date] = cd;
    return m;
  }, [campDates]);

  const ageGroups = useMemo(() => (
    apiAgeGroups && apiAgeGroups.length
      ? apiAgeGroups.map((g) => ({ min: g.min_age, max: g.max_age, name: lang === "fr" ? g.name_fr : (g.name_en || g.name_fr) }))
      : t.booking.ageGroups
  ), [apiAgeGroups, lang, t.booking.ageGroups]);

  const loadAvailability = useCallback(async () => {
    try {
      const [c, a, cd] = await Promise.all([
        axios.get(`${API}/config`),
        axios.get(`${API}/availability`),
        axios.get(`${API}${datesEndpoint}`),
      ]);
      setConfig(c.data);
      setBooked(a.data.booked || {});
      const dates = cd.data?.dates || [];
      const todayStr = iso(new Date());
      const raw = dates.length ? dates : (isAtelier ? [] : buildDefaultDates());
      setCampDates(raw.filter((d) => d.date >= todayStr));
    } catch (e) {
      setCampDates(isAtelier ? [] : buildDefaultDates());
    }
  }, [datesEndpoint, isAtelier]);

  useEffect(() => { loadAvailability(); }, [loadAvailability]);

  const validEmailStr = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);

  useEffect(() => {
    if (!validEmailStr(parent.email)) { setIsMember(false); return; }
    setCheckingMember(true);
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/member-status`, { params: { email: parent.email.trim() } });
        setIsMember(!!res.data.is_member);
      } catch (e) { setIsMember(false); }
      setCheckingMember(false);
    }, 500);
    return () => clearTimeout(timer);
  }, [parent.email]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) return;
    setPayState("processing");
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setPayState("success");
          setBookingResult(res.data.booking);
          return;
        }
        if (res.data.status === "expired") { setPayState("failed"); return; }
      } catch (e) { console.warn("Vérification du paiement — nouvelle tentative", e); }
      attempts += 1;
      if (attempts >= 10) { setPayState("pending"); return; }
      setTimeout(poll, 2000);
    };
    poll();
  }, []);

  const totalSlots = useMemo(
    () => Object.values(selections).reduce((sum, set) => sum + set.size, 0),
    [selections]
  );
  const priceForCount = (n) => {
    if (!isMember) return config?.non_member_price ?? 65;
    const threshold = config?.discount_threshold ?? 10;
    return n >= threshold ? (config?.member_discount_price ?? 40) : (config?.member_price ?? 45);
  };
  const total = useMemo(
    () => Object.values(selections).reduce((sum, set) => sum + set.size * priceForCount(set.size), 0),
    [selections, isMember]
  );
  const anyChildDiscount = useMemo(
    () => isMember && Object.values(selections).some((set) => set.size >= 10),
    [selections, isMember]
  );
  const unitPrice = totalSlots > 0 ? Math.round((total / totalSlots) * 100) / 100 : (isMember ? 45 : 50);

  const selectedCountForSlot = useCallback(
    (key) => Object.values(selections).reduce((n, set) => n + (set.has(key) ? 1 : 0), 0),
    [selections]
  );

  const toggleFullDay = (dateStr) => {
    setSelections((prev) => {
      const set = new Set(prev[activeChild] || []);
      const mKey = `${dateStr}|morning`;
      const aKey = `${dateStr}|afternoon`;
      const hasBoth = set.has(mKey) && set.has(aKey);
      if (hasBoth) {
        set.delete(mKey); set.delete(aKey);
      } else {
        for (const [key, period] of [[mKey, "morning"], [aKey, "afternoon"]]) {
          if (!set.has(key)) {
            const serverBooked = booked[dateStr]?.[period] || 0;
            const taken = serverBooked + selectedCountForSlot(key);
            if (taken < (campMap[dateStr]?.capacity || config?.capacity || 6)) set.add(key);
          }
        }
      }
      return { ...prev, [activeChild]: set };
    });
  };

  const toggleSlot = (dateStr, period) => {
    const key = `${dateStr}|${period}`;
    setSelections((prev) => {
      const set = new Set(prev[activeChild] || []);
      if (set.has(key)) {
        set.delete(key);
      } else {
        const serverBooked = booked[dateStr]?.[period] || 0;
        const taken = serverBooked + selectedCountForSlot(key);
        if (taken >= (campMap[dateStr]?.capacity || config?.capacity || 6)) return prev;
        set.add(key);
      }
      return { ...prev, [activeChild]: set };
    });
  };

  const addChild = () => {
    const id = childSeq++;
    setChildren((c) => [...c, { id, first_name: "", last_name: "", dob: "", allergies: "" }]);
    setSelections((s) => ({ ...s, [id]: new Set() }));
    setActiveChild(id);
  };

  const removeChild = (id) => {
    if (children.length === 1) return;
    setChildren((c) => c.filter((x) => x.id !== id));
    setSelections((s) => { const ns = { ...s }; delete ns[id]; return ns; });
    if (activeChild === id) setActiveChild(children.find((x) => x.id !== id).id);
  };

  const updateChild = (id, field, value) =>
    setChildren((c) => c.map((x) => (x.id === id ? { ...x, [field]: value } : x)));

  const active = children.find((c) => c.id === activeChild) || children[0];
  const activeAge = computeAge(active?.dob);
  const activeGroup = groupFor(activeAge, ageGroups);

  const childLabel = (c, i) => (c.first_name || "").trim() || `${t.booking.childName} ${i + 1}`;

  const handlePay = async () => {
    setError("");
    if (!parent.name.trim() || !validEmailStr(parent.email)) {
      setError(t.booking.requiredFields);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    if (totalSlots === 0) { setError(t.booking.emptyCart); return; }
    if (!consents.resident) {
      setError(t.booking.residentRequired);
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    const payloadChildren = children
      .map((c) => ({
        first_name: (c.first_name || "").trim(),
        last_name: (c.last_name || "").trim(),
        dob: (c.dob || "").trim(),
        name: `${(c.first_name || "").trim()} ${(c.last_name || "").trim()}`.trim() || (lang === "fr" ? "Enfant" : "Child"),
        allergies: (c.allergies || "").trim(),
        slots: Array.from(selections[c.id] || []).map((k) => {
          const [date, period] = k.split("|");
          return { date, period };
        }),
      }))
      .filter((c) => c.slots.length > 0);

    setLoading(true);
    try {
      const res = await axios.post(`${API}/checkout`, {
        parent: { name: parent.name.trim(), email: parent.email.trim(), phone: parent.phone.trim() },
        children: payloadChildren,
        emergency_name: emergency.name.trim(),
        emergency_phone: emergency.phone.trim(),
        consents: {
          image_rights: consents.image_rights,
          outdoor_activities: consents.outdoor_activities,
          swimming: consents.swimming,
          emergency_transport: consents.emergency_transport,
          resident: consents.resident,
          authorized_pickup: consents.authorized_pickup.trim(),
        },
        return_path: returnPath,
        origin_url: window.location.origin,
      });
      window.location.href = res.data.url;
    } catch (e) {
      setLoading(false);
      setError(e?.response?.data?.detail || t.booking.payError);
    }
  };

  // ---------- Payment return screens ----------
  if (payState === "processing") {
    return (
      <ReturnShell>
        <Loader2 className="animate-spin text-[#e07a5f]" size={48} />
        <h1 className="font-head font-bold text-2xl text-[#6f7d54] mt-5">{t.booking.processing}</h1>
      </ReturnShell>
    );
  }
  if (payState === "success") {
    return (
      <ReturnShell>
        <div className="w-20 h-20 rounded-full bg-[#5e7d52]/15 flex items-center justify-center mb-2">
          <PartyPopper className="text-[#5e7d52]" size={40} />
        </div>
        <h1 className="font-head font-extrabold text-3xl text-[#6f7d54]">{t.booking.success}</h1>
        <p className="font-body text-[#3a4d68] mt-3 max-w-md">{t.booking.successText}</p>
        {bookingResult && (
          <div data-testid="booking-summary-card" className="mt-7 w-full max-w-md bg-white rounded-3xl border border-[#6f7d54]/10 p-6 text-left">
            <p className="font-body text-sm text-[#3a4d68]">{bookingResult.parent_name} · {bookingResult.parent_email}</p>
            <div className="mt-3 space-y-3">
              {bookingResult.children.map((c, i) => (
                <div key={i}>
                  <p className="font-head font-semibold text-[#6f7d54]">{c.name}{c.age_group_fr ? <span className="font-body text-sm text-[#2e8b9e]"> · {lang === "fr" ? c.age_group_fr : (c.age_group_en || c.age_group_fr)}</span> : null}</p>
                  <p className="font-body text-sm text-[#3a4d68]">
                    {c.slots.map((s) => `${s.date} (${s.period === "morning" ? t.booking.morning : t.booking.afternoon})`).join(" · ")}
                  </p>
                </div>
              ))}
            </div>
            <div className="border-t border-[#6f7d54]/10 mt-4 pt-4 flex justify-between font-head font-bold text-[#6f7d54]">
              <span>{t.booking.total}</span>
              <span>{bookingResult.amount}€</span>
            </div>
          </div>
        )}
        <a href="/reservation" data-testid="new-booking-btn" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#6f7d54] text-white font-bold px-7 py-3.5">
          <ArrowLeft size={18} /> {t.booking.backToBooking}
        </a>
      </ReturnShell>
    );
  }
  if (payState === "pending") {
    return (
      <ReturnShell>
        <div className="w-20 h-20 rounded-full bg-[#e0b36a]/20 flex items-center justify-center mb-2">
          <Loader2 className="animate-spin text-[#8a6a1f]" size={40} />
        </div>
        <h1 className="font-head font-bold text-2xl text-[#6f7d54]" data-testid="pay-pending-title">{t.booking.paymentPending}</h1>
        <p className="font-body text-[#3a4d68] mt-3 max-w-md">{t.booking.paymentPendingText}</p>
        <a href="/mon-compte" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#6f7d54] text-white font-bold px-7 py-3.5">{t.booking.viewMyAccount}</a>
      </ReturnShell>
    );
  }
  if (payState === "failed") {
    return (
      <ReturnShell>
        <div className="w-20 h-20 rounded-full bg-[#e63946]/12 flex items-center justify-center mb-2">
          <AlertTriangle className="text-[#e63946]" size={40} />
        </div>
        <h1 className="font-head font-bold text-2xl text-[#6f7d54]">{t.booking.failed}</h1>
        <a href="/reservation" className="mt-7 inline-flex items-center gap-2 rounded-full bg-[#e07a5f] text-white font-bold px-7 py-3.5">
          <ArrowLeft size={18} /> {t.booking.backToBooking}
        </a>
      </ReturnShell>
    );
  }

  // ---------- Main booking form ----------
  return (
    <div data-testid="booking-page" className={embedded ? "bg-[#fbf6ec]" : "bg-[#fbf6ec] min-h-screen"}>
      {!embedded && (
        <div className="relative pt-32 pb-14 bg-[#6f7d54] overflow-hidden">
          <img src={PHOTO_NICHE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#6f7d54] to-[#6f7d54]/60" />
          <div className="relative z-10 max-w-6xl mx-auto px-5 lg:px-10">
            <h1 className="font-head font-extrabold text-white text-3xl md:text-5xl tracking-tight">{pageTitle}</h1>
            <p className="font-body text-[#f7efe0]/85 mt-3 flex items-center gap-2">
              <CalendarDays size={18} className="text-[#e0b36a]" /> {pageSubtitle}
            </p>
          </div>
        </div>
      )}
      {embedded && (
        <div className="max-w-6xl mx-auto px-5 lg:px-10 pt-4">
          <h2 className="font-head font-extrabold text-[#6f7d54] text-2xl md:text-3xl tracking-tight">{pageTitle}</h2>
          <p className="font-body text-[#3a4d68] mt-2 flex items-center gap-2">
            <CalendarDays size={18} className="text-[#e07a5f]" /> {pageSubtitle}
          </p>
        </div>
      )}

      <div className="max-w-6xl mx-auto px-5 lg:px-10 py-12 grid lg:grid-cols-[1fr_360px] gap-8 items-start">
        {/* LEFT */}
        <div className="space-y-8">
          {error && (
            <div data-testid="booking-error" className="bg-[#e63946]/10 border border-[#e63946]/30 text-[#b5232f] rounded-2xl px-5 py-4 font-body font-semibold">
              {error}
            </div>
          )}

          {/* Parent */}
          <section className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6 md:p-8">
            <h2 className="font-head font-bold text-xl text-[#6f7d54] mb-5">{t.booking.parent}</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label={t.booking.parentName} testid="parent-name" value={parent.name} onChange={(v) => setParent({ ...parent, name: v })} />
              <Field label={t.booking.email} testid="parent-email" type="email" value={parent.email} onChange={(v) => setParent({ ...parent, email: v })} />
              <Field label={t.booking.phone} testid="parent-phone" value={parent.phone} onChange={(v) => setParent({ ...parent, phone: v })} />
            </div>
            <div className="mt-5 pt-5 border-t border-[#6f7d54]/10">
              <p className="font-head font-semibold text-[#6f7d54] mb-3">{t.booking.emergencyTitle}</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t.booking.emergencyName} testid="emergency-name" value={emergency.name} onChange={(v) => setEmergency({ ...emergency, name: v })} />
                <Field label={t.booking.emergencyPhone} testid="emergency-phone" value={emergency.phone} onChange={(v) => setEmergency({ ...emergency, phone: v })} />
              </div>
            </div>
            {validEmailStr(parent.email) && !checkingMember && (
              isMember ? (
                <div data-testid="member-badge" className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#5e7d52]/12 text-[#4a6742] font-bold text-sm px-4 py-2">
                  <BadgeCheck size={16} /> {t.booking.memberBadge}
                </div>
              ) : (
                <div data-testid="nonmember-badge" className="mt-4 flex flex-wrap items-center gap-3 bg-[#e0b36a]/15 rounded-2xl px-4 py-3">
                  <span className="font-body text-sm text-[#3a4d68]">{t.booking.memberNudge}</span>
                  <a href="/adhesion" className="text-sm font-bold text-[#cf6a50] underline whitespace-nowrap">{t.booking.becomeMember}</a>
                </div>
              )
            )}
          </section>

          {/* Children */}
          <section className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6 md:p-8">
            <div className="flex items-center justify-between mb-5">
              <h2 className="font-head font-bold text-xl text-[#6f7d54]">{t.booking.children}</h2>
              <button onClick={addChild} data-testid="add-child-btn" className="inline-flex items-center gap-1.5 rounded-full bg-[#e07a5f]/12 text-[#cf6a50] font-bold text-sm px-4 py-2 hover:bg-[#e07a5f]/20 transition-colors">
                <Plus size={16} /> {t.booking.addChild}
              </button>
            </div>

            {/* chips to switch active child */}
            <div className="flex flex-wrap gap-2.5 mb-6">
              {children.map((c, i) => {
                const count = selections[c.id]?.size || 0;
                return (
                  <button
                    key={c.id}
                    type="button"
                    data-testid={`child-chip-${i}`}
                    onClick={() => setActiveChild(c.id)}
                    className={`rounded-2xl border-2 px-4 py-2.5 flex items-center gap-2.5 transition-all ${
                      activeChild === c.id ? "border-[#e07a5f] bg-[#e07a5f]/8" : "border-[#6f7d54]/10 bg-[#fbf6ec]"
                    }`}
                  >
                    <span className="font-body font-semibold text-[#6f7d54]">{childLabel(c, i)}</span>
                    <span className="text-xs font-bold rounded-full bg-[#6f7d54] text-white px-2 py-0.5">{count}</span>
                    {children.length > 1 && (
                      <span onClick={(e) => { e.stopPropagation(); removeChild(c.id); }} data-testid={`remove-child-${i}`} className="text-[#3a4d68]/50 hover:text-[#e63946]">
                        <Trash2 size={15} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* active child details */}
            <div className="bg-[#fbf6ec]/70 rounded-2xl border border-[#6f7d54]/10 p-5">
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label={t.booking.childFirstName} testid="child-first-name" value={active.first_name} onChange={(v) => updateChild(active.id, "first_name", v)} />
                <Field label={t.booking.childLastName} testid="child-last-name" value={active.last_name} onChange={(v) => updateChild(active.id, "last_name", v)} />
                <Field label={t.booking.dob} testid="child-dob" type="date" value={active.dob} onChange={(v) => updateChild(active.id, "dob", v)} />
                <div className="flex flex-col justify-end">
                  <span className="font-body font-semibold text-sm text-[#3a4d68] block mb-1.5">{t.booking.groupHint}</span>
                  <div data-testid="child-group" className="rounded-xl px-4 py-3 font-body font-bold text-sm border border-[#2e8b9e]/25 bg-[#2e8b9e]/8 text-[#2e8b9e]">
                    {activeAge == null ? t.booking.groupUnknown : (activeGroup ? `${activeGroup.name} · ${activeAge} ${t.booking.yearsShort}` : `${t.booking.outOfRange} (${activeAge} ${t.booking.yearsShort})`)}
                  </div>
                </div>
              </div>
              <label className="block mt-4">
                <span className="font-body font-semibold text-sm text-[#3a4d68] block mb-1.5">{t.booking.allergies}</span>
                <input
                  data-testid="child-allergies"
                  value={active.allergies || ""}
                  onChange={(e) => updateChild(active.id, "allergies", e.target.value)}
                  placeholder={t.booking.allergiesPlaceholder}
                  className="w-full rounded-xl border border-[#6f7d54]/15 bg-white px-4 py-3 font-body text-[#6f7d54] outline-none focus:border-[#e07a5f] transition-colors"
                />
              </label>
            </div>
          </section>

          {/* Calendar */}
          <section className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <h2 className="font-head font-bold text-xl text-[#6f7d54]">{t.booking.selectFor}</h2>
              <span className="font-script text-2xl text-[#e07a5f]">{childLabel(active, children.indexOf(active))}</span>
            </div>
            <p className="font-body text-sm text-[#3a4d68]/70 mb-6">{t.booking.weekend}</p>

            {!isAtelier && visiblePeriods.length > 0 && (
              <div data-testid="period-selector" className="flex flex-wrap gap-2 mb-7">
                {visiblePeriods.map((p) => (
                  <button
                    key={p.id}
                    data-testid={`period-tab-${p.id}`}
                    onClick={() => setActivePeriod(p.id)}
                    className={`rounded-full px-4 py-2 text-sm font-bold border transition-all ${activePeriod === p.id ? "bg-[#2e8b9e] text-white border-[#2e8b9e]" : "bg-white text-[#2e8b9e] border-[#2e8b9e]/30 hover:bg-[#2e8b9e]/8"}`}
                  >
                    {lang === "fr" ? p.name_fr : (p.name_en || p.name_fr)}
                  </button>
                ))}
              </div>
            )}

            <div className="space-y-9">
              {months.length === 0 && (
                <p data-testid="no-dates" className="font-body text-sm text-[#3a4d68]/70">{t.booking.noDates}</p>
              )}
              {months.map((m) => (
                <div key={`${m.year}-${m.month}`}>
                  <h3 className="font-head font-bold text-[#2e8b9e] text-lg mb-4">{t.booking.months[m.month]} {m.year}</h3>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {m.days.map((day) => {
                      const dateStr = day.date;
                      const periods = ["morning", "afternoon"].filter((p) => day[p]);
                      return (
                        <div key={dateStr} className="rounded-2xl border border-[#6f7d54]/10 p-3 bg-[#fbf6ec]/60">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-body font-bold text-[#6f7d54] text-sm">
                              {t.booking.days[day.dateObj.getDay() - 1]} {day.dateObj.getDate()}
                            </span>
                            {day.morning && day.afternoon && (() => {
                              const both = selections[activeChild]?.has(`${dateStr}|morning`) && selections[activeChild]?.has(`${dateStr}|afternoon`);
                              return (
                                <button
                                  data-testid={`fullday-${dateStr}`}
                                  onClick={() => toggleFullDay(dateStr)}
                                  className={`text-[11px] font-bold rounded-full px-2.5 py-0.5 border transition-all ${both ? "bg-[#2e8b9e] text-white border-[#2e8b9e]" : "bg-white text-[#2e8b9e] border-[#2e8b9e]/40 hover:bg-[#2e8b9e]/8"}`}
                                >
                                  {t.booking.fullDay}
                                </button>
                              );
                            })()}
                          </div>
                          {day.theme ? <div className="text-[11px] font-bold text-[#2e8b9e] mb-1.5 truncate">{day.theme}</div> : null}
                          <div className="flex gap-2">
                            {periods.map((period) => {
                              const key = `${dateStr}|${period}`;
                              const isSel = selections[activeChild]?.has(key);
                              const serverBooked = booked[dateStr]?.[period] || 0;
                              const cap = day.capacity || config?.capacity || 6;
                              const taken = serverBooked + selectedCountForSlot(key) - (isSel ? 1 : 0);
                              const remaining = cap - taken;
                              const full = remaining <= 0 && !isSel;
                              return (
                                <button
                                  key={period}
                                  data-testid={`slot-${dateStr}-${period}`}
                                  disabled={full}
                                  onClick={() => toggleSlot(dateStr, period)}
                                  className={`flex-1 rounded-xl px-2 py-2 text-xs font-bold transition-all border ${
                                    isSel
                                      ? "bg-[#e07a5f] text-white border-[#e07a5f]"
                                      : full
                                      ? "bg-[#6f7d54]/5 text-[#3a4d68]/40 border-transparent cursor-not-allowed"
                                      : "bg-white text-[#6f7d54] border-[#6f7d54]/10 hover:border-[#e07a5f] hover:bg-[#e07a5f]/5"
                                  }`}
                                >
                                  <span className="flex items-center justify-center gap-1">
                                    {isSel && <Check size={12} />}
                                    {period === "morning" ? t.booking.morning : t.booking.afternoon}
                                  </span>
                                  <span className="block mt-0.5 font-semibold opacity-80">
                                    {full ? t.booking.full : `${remaining} ${t.booking.remaining}`}
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Consents / authorizations */}
          <section data-testid="consents-section" className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6 md:p-8">
            <div className="flex items-center gap-2 mb-1">
              <ShieldCheck size={20} className="text-[#5e7d52]" />
              <h2 className="font-head font-bold text-xl text-[#6f7d54]">{t.booking.consentsTitle}</h2>
            </div>
            <p className="font-body text-sm text-[#3a4d68]/70 mb-5">{t.booking.consentsIntro}</p>

            <label className="block mb-5">
              <span className="font-body font-semibold text-sm text-[#3a4d68] block mb-1.5">{t.booking.authorizedPickup}</span>
              <input
                data-testid="authorized-pickup"
                value={consents.authorized_pickup}
                onChange={(e) => setConsents({ ...consents, authorized_pickup: e.target.value })}
                placeholder={t.booking.authorizedPickupPlaceholder}
                className="w-full rounded-xl border border-[#6f7d54]/15 bg-white px-4 py-3 font-body text-[#6f7d54] outline-none focus:border-[#e07a5f] transition-colors"
              />
            </label>

            <div className="space-y-3">
              <YesNo testid="consent-image" label={t.booking.consentImage} value={consents.image_rights} onChange={(v) => setConsents({ ...consents, image_rights: v })} yes={t.booking.consentYes} no={t.booking.consentNo} />
              <YesNo testid="consent-outdoor" label={t.booking.consentOutdoor} value={consents.outdoor_activities} onChange={(v) => setConsents({ ...consents, outdoor_activities: v })} yes={t.booking.consentYes} no={t.booking.consentNo} />
              <YesNo testid="consent-swim" label={t.booking.consentSwim} value={consents.swimming} onChange={(v) => setConsents({ ...consents, swimming: v })} yes={t.booking.consentYes} no={t.booking.consentNo} />
              <YesNo testid="consent-transport" label={t.booking.consentTransport} value={consents.emergency_transport} onChange={(v) => setConsents({ ...consents, emergency_transport: v })} yes={t.booking.consentYes} no={t.booking.consentNo} />
            </div>

            <label className="mt-5 flex items-start gap-3 cursor-pointer bg-[#e07a5f]/8 rounded-2xl border border-[#e07a5f]/25 p-4">
              <input
                type="checkbox"
                data-testid="consent-resident"
                checked={consents.resident}
                onChange={(e) => setConsents({ ...consents, resident: e.target.checked })}
                className="mt-0.5 w-5 h-5 accent-[#e07a5f]"
              />
              <span className="font-body text-sm text-[#6f7d54]">{t.booking.residentConfirm} <span className="text-[#cf6a50] font-bold">*</span></span>
            </label>
          </section>
        </div>

        {/* RIGHT — cart */}
        <aside className="lg:sticky lg:top-24">
          <div data-testid="cart-summary" className="bg-white rounded-3xl border border-[#6f7d54]/10 p-6 shadow-lg">
            <h2 className="font-head font-bold text-xl text-[#6f7d54] mb-5">{t.booking.summary}</h2>

            <div className="space-y-3 mb-5">
              {children.map((c, i) => {
                const count = selections[c.id]?.size || 0;
                if (count === 0) return null;
                return (
                  <div key={c.id} className="flex justify-between font-body text-[#3a4d68]">
                    <span>{childLabel(c, i)}</span>
                    <span className="font-bold">{count} ×</span>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-[#6f7d54]/10 pt-4 space-y-2 font-body text-[#3a4d68]">
              <div className="flex justify-between">
                <span>{t.booking.totalSessions}</span>
                <span data-testid="total-sessions" className="font-bold text-[#6f7d54]">{totalSlots}</span>
              </div>
              <div className="flex justify-between">
                <span>{t.booking.unitPrice}</span>
                <span data-testid="unit-price" className="font-bold text-[#6f7d54]">{unitPrice}€</span>
              </div>
            </div>

            {!isMember && totalSlots > 0 ? (
              <p className="mt-3 text-sm font-semibold text-[#cf6a50] bg-[#e07a5f]/10 rounded-xl px-3 py-2">{t.booking.memberNudge}</p>
            ) : anyChildDiscount ? (
              <p className="mt-3 text-sm font-bold text-[#5e7d52] bg-[#5e7d52]/10 rounded-xl px-3 py-2">{t.booking.discountActive}</p>
            ) : isMember && totalSlots > 0 ? (
              <p className="mt-3 text-sm font-semibold text-[#cf6a50] bg-[#e07a5f]/10 rounded-xl px-3 py-2">{t.booking.discountHint}</p>
            ) : null}

            <div className="flex items-start gap-2 mt-4 text-[#3a4d68] font-body text-sm bg-[#e0b36a]/15 rounded-xl px-3 py-2.5">
              <Sandwich size={18} className="text-[#cf6a50] shrink-0 mt-0.5" /> {t.booking.lunch}
            </div>

            <div className="flex justify-between items-end mt-5 mb-5">
              <span className="font-head font-bold text-lg text-[#6f7d54]">{t.booking.total}</span>
              <span data-testid="cart-total" className="font-head font-extrabold text-3xl text-[#e07a5f]">{total}€</span>
            </div>

            <button
              onClick={handlePay}
              disabled={loading || totalSlots === 0}
              data-testid="pay-btn"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#e07a5f] hover:bg-[#cf6a50] disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold text-lg px-6 py-4 transition-colors"
            >
              {loading ? <><Loader2 className="animate-spin" size={20} /> {t.booking.paying}</> : <><CreditCard size={20} /> {t.booking.pay}</>}
            </button>
            {settings.rib_iban && (
              <div data-testid="rib-info" className="mt-4 rounded-2xl border border-[#6f7d54]/15 bg-[#fbf6ec]/70 p-4 text-sm text-[#3a4d68]">
                <p className="font-head font-bold text-[#6f7d54] mb-1">Ou par virement bancaire</p>
                {settings.bank_name && <p><span className="font-semibold">Banque :</span> {settings.bank_name}</p>}
                {settings.rib_holder && <p><span className="font-semibold">Titulaire :</span> {settings.rib_holder}</p>}
                <p className="break-all"><span className="font-semibold">IBAN :</span> {settings.rib_iban}</p>
                {settings.rib_bic && <p><span className="font-semibold">BIC :</span> {settings.rib_bic}</p>}
              </div>
            )}
          </div>
        </aside>
      </div>
    </div>
  );
}

function YesNo({ label, value, onChange, yes, no, testid }) {
  return (
    <div data-testid={testid} className="flex items-center justify-between gap-4 rounded-2xl border border-[#6f7d54]/10 bg-[#fbf6ec]/40 px-4 py-3">
      <span className="font-body text-sm text-[#6f7d54]">{label}</span>
      <div className="flex gap-1.5 shrink-0">
        <button type="button" data-testid={`${testid}-yes`} onClick={() => onChange(true)} className={`rounded-full px-4 py-1.5 text-sm font-bold border transition-all ${value ? "bg-[#5e7d52] text-white border-[#5e7d52]" : "bg-white text-[#3a4d68] border-[#6f7d54]/15"}`}>{yes}</button>
        <button type="button" data-testid={`${testid}-no`} onClick={() => onChange(false)} className={`rounded-full px-4 py-1.5 text-sm font-bold border transition-all ${!value ? "bg-[#cf6a50] text-white border-[#cf6a50]" : "bg-white text-[#3a4d68] border-[#6f7d54]/15"}`}>{no}</button>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", testid }) {
  return (
    <label className="block">
      <span className="font-body font-semibold text-sm text-[#3a4d68] block mb-1.5">{label}</span>
      <input
        type={type}
        value={value}
        data-testid={testid}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-xl border border-[#6f7d54]/15 bg-white px-4 py-3 font-body text-[#6f7d54] outline-none focus:border-[#e07a5f] transition-colors"
      />
    </label>
  );
}

function ReturnShell({ children }) {
  return (
    <div className="min-h-screen bg-[#fbf6ec] flex flex-col items-center justify-center text-center px-6 pt-24 pb-16">
      {children}
    </div>
  );
}
